import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';

export const CartPage = () => {
    const { cart, updateQuantity, removeFromCart, loading } = useCart();
    const navigate = useNavigate();

    const subtotal = cart.total;
    const shipping = subtotal >= 999 ? 0 : 99;
    const total = subtotal + shipping;

    if (cart.items.length === 0) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen" data-testid="cart-empty">
                    <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
                        <ShoppingBag className="w-20 h-20 mx-auto mb-6 text-muted-foreground" />
                        <h1 className="font-heading text-4xl md:text-6xl mb-4">YOUR CART IS EMPTY</h1>
                        <p className="text-muted-foreground mb-8">Looks like you haven't added anything yet.</p>
                        <Link
                            to="/products"
                            className="inline-block bg-lime text-black px-8 py-4 text-xs font-bold tracking-[0.14em] uppercase"
                        >
                            START SHOPPING
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="cart-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <h1 className="font-heading text-5xl md:text-7xl mb-8 md:mb-12">YOUR CART</h1>

                    <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cart.items.map((item, index) => (
                                <motion.div
                                    key={`${item.product_id}-${item.size}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="flex gap-4 p-4 bg-card border border-border"
                                    data-testid={`cart-item-${item.product_id}`}
                                >
                                    {/* Image */}
                                    <div className="w-24 h-32 md:w-32 md:h-40 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center flex-shrink-0">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-4xl opacity-50">👕</span>
                                        )}
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex justify-between mb-2">
                                            <Link 
                                                to={`/products/${item.product_id}`}
                                                className="font-medium hover:text-lime transition-colors"
                                            >
                                                {item.name}
                                            </Link>
                                            <button
                                                onClick={() => removeFromCart(item.product_id, item.size)}
                                                className="text-muted-foreground hover:text-destructive transition-colors"
                                                data-testid={`remove-${item.product_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {item.size && (
                                            <span className="text-xs text-muted-foreground mb-2">
                                                Size: {item.size}
                                            </span>
                                        )}

                                        <div className="mt-auto flex items-center justify-between">
                                            {/* Quantity */}
                                            <div className="flex items-center border border-border">
                                                <button
                                                    onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size)}
                                                    disabled={loading}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-secondary transition-colors"
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="w-8 h-8 flex items-center justify-center text-sm font-medium">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size)}
                                                    disabled={loading}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-secondary transition-colors"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>

                                            {/* Price */}
                                            <span className="font-bold">
                                                ₹{(item.price * item.quantity).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-card border border-border p-6 sticky top-32" data-testid="cart-summary">
                                <h2 className="font-heading text-2xl mb-6">ORDER SUMMARY</h2>

                                <div className="space-y-4 pb-6 border-b border-border">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Subtotal</span>
                                        <span>₹{subtotal.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Shipping</span>
                                        <span className={shipping === 0 ? 'text-lime' : ''}>
                                            {shipping === 0 ? 'FREE' : `₹${shipping}`}
                                        </span>
                                    </div>
                                    {shipping > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            Add ₹{(999 - subtotal).toLocaleString()} more for free shipping
                                        </p>
                                    )}
                                </div>

                                <div className="flex justify-between py-6 border-b border-border">
                                    <span className="font-heading text-xl">TOTAL</span>
                                    <span className="font-heading text-xl" data-testid="cart-total">₹{total.toLocaleString()}</span>
                                </div>

                                {/* Discount code */}
                                <div className="py-6 border-b border-border">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Discount code"
                                            className="flex-1 bg-background border border-border px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-lime"
                                            data-testid="discount-input"
                                        />
                                        <button className="px-4 py-2 border border-border text-xs font-bold tracking-wider uppercase hover:border-lime hover:text-lime transition-colors">
                                            APPLY
                                        </button>
                                    </div>
                                </div>

                                <button
                                    onClick={() => navigate('/checkout')}
                                    className="w-full bg-lime text-black py-4 mt-6 text-sm font-bold tracking-[0.14em] uppercase flex items-center justify-center gap-2 hover:tracking-[0.2em] transition-all duration-300"
                                    data-testid="checkout-btn"
                                >
                                    PROCEED TO CHECKOUT
                                    <ArrowRight className="w-4 h-4" />
                                </button>

                                <Link
                                    to="/products"
                                    className="block w-full text-center py-4 mt-2 text-xs tracking-[0.1em] uppercase text-muted-foreground hover:text-lime transition-colors"
                                >
                                    CONTINUE SHOPPING
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
